import { normalizeMemberPostalCode } from "../location/memberLocation";

export type AustraliaJurisdictionCode =
  | "NSW"
  | "VIC"
  | "QLD"
  | "WA"
  | "SA"
  | "TAS"
  | "ACT"
  | "NT";

export type AustraliaAppendixLink = {
  label: string;
  url: string;
};

export const AUSTRALIA_APPENDIX_ROUTE_LABELS = {
  tenancy: "Appendix A — Tenancy authority in your state or territory",
  socialHousing: "Appendix B — Social housing in your state or territory",
  educationStart: "Appendix C — Education starting point in your state or territory",
  homeEducation: "Appendix C — Home education in your state or territory",
  legalAid: "Appendix D — Legal aid in your state or territory",
} as const;

const AUSTRALIA_JURISDICTION_LABELS: Record<AustraliaJurisdictionCode, string> = {
  NSW: "New South Wales",
  VIC: "Victoria",
  QLD: "Queensland",
  WA: "Western Australia",
  SA: "South Australia",
  TAS: "Tasmania",
  ACT: "Australian Capital Territory",
  NT: "Northern Territory",
};

const AUSTRALIA_JURISDICTION_LINKS: Record<
  AustraliaJurisdictionCode,
  {
    tenancy: AustraliaAppendixLink;
    socialHousing: AustraliaAppendixLink;
    educationStart: AustraliaAppendixLink;
    homeEducation: AustraliaAppendixLink;
    legalAid: AustraliaAppendixLink;
  }
> = {
  NSW: {
    tenancy: {
      label: "NSW Government — Renting a place to live",
      url: "https://www.nsw.gov.au/housing-and-construction/renting-a-place-to-live",
    },
    socialHousing: {
      label: "Homes NSW — Applying for social housing",
      url: "https://www.nsw.gov.au/departments-and-agencies/homes-nsw/applying-for-social-housing",
    },
    educationStart: {
      label: "NSW Government — School enrolment",
      url: "https://www.nsw.gov.au/education-and-training/primary-school/starting-school/enrolment",
    },
    homeEducation: {
      label: "NSW Education Standards Authority — Home schooling",
      url: "https://www.nsw.gov.au/education-and-training/nesa/curriculum/home-schooling",
    },
    legalAid: {
      label: "Legal Aid NSW",
      url: "https://www.legalaid.nsw.gov.au/",
    },
  },
  VIC: {
    tenancy: {
      label: "Consumer Affairs Victoria — Renting",
      url: "https://www.consumer.vic.gov.au/housing/renting",
    },
    socialHousing: {
      label: "Victorian Housing Register",
      url: "https://www.housing.vic.gov.au/victorian-housing-register",
    },
    educationStart: {
      label: "Victoria — Enrolling in school",
      url: "https://www.vic.gov.au/enrolling-foundation-prep",
    },
    homeEducation: {
      label: "VRQA — Home schooling",
      url: "https://www.vrqa.vic.gov.au/home/Pages/homeschooling.aspx",
    },
    legalAid: {
      label: "Victoria Legal Aid",
      url: "https://www.legalaid.vic.gov.au/",
    },
  },
  QLD: {
    tenancy: {
      label: "Residential Tenancies Authority Queensland",
      url: "https://www.rta.qld.gov.au/",
    },
    socialHousing: {
      label: "Queensland Government — Apply for housing",
      url: "https://www.qld.gov.au/housing/public-community-housing/apply-for-housing",
    },
    educationStart: {
      label: "Queensland Government — School enrolment",
      url: "https://www.qld.gov.au/education/schools/find/enrolment",
    },
    homeEducation: {
      label: "Queensland Government — Home education",
      url: "https://www.qld.gov.au/education/schools/find/home-education",
    },
    legalAid: {
      label: "Legal Aid Queensland",
      url: "https://www.legalaid.qld.gov.au/",
    },
  },
  WA: {
    tenancy: {
      label: "WA Consumer Protection — Renting a home",
      url: "https://www.consumerprotection.wa.gov.au/renting-home",
    },
    socialHousing: {
      label: "WA Government — Public housing",
      url: "https://www.wa.gov.au/organisation/department-of-housing-and-works/public-housing",
    },
    educationStart: {
      label: "WA Department of Education — Enrolling in school",
      url: "https://www.education.wa.edu.au/enrolling-in-school",
    },
    homeEducation: {
      label: "WA Department of Education — Home education",
      url: "https://www.education.wa.edu.au/home-education",
    },
    legalAid: {
      label: "Legal Aid WA",
      url: "https://www.legalaid.wa.gov.au/",
    },
  },
  SA: {
    tenancy: {
      label: "Consumer and Business Services SA — Renting and letting",
      url: "https://www.cbs.sa.gov.au/campaigns/renting-in-sa",
    },
    socialHousing: {
      label: "SA.GOV.AU — Register for housing",
      url: "https://www.sa.gov.au/topics/housing/public-and-community-housing/renting-and-letting/register-for-housing",
    },
    educationStart: {
      label: "South Australia — Enrol in school",
      url: "https://www.education.sa.gov.au/parents-and-families/enrol-school-or-preschool/enrol-school",
    },
    homeEducation: {
      label: "South Australia — Home education",
      url: "https://www.education.sa.gov.au/parents-and-families/curriculum-and-learning/home-education",
    },
    legalAid: {
      label: "Legal Services Commission of South Australia",
      url: "https://lsc.sa.gov.au/",
    },
  },
  TAS: {
    tenancy: {
      label: "Tasmania — Renting",
      url: "https://www.cbos.tas.gov.au/topics/housing/renting",
    },
    socialHousing: {
      label: "Service Tasmania — Apply for social housing",
      url: "https://www.service.tas.gov.au/services/housing-and-property/social-and-affordable-housing/apply-for-social-housing",
    },
    educationStart: {
      label: "Tasmania — School enrolment",
      url: "https://www.decyp.tas.gov.au/learning/find-your-local-school/enrolment/",
    },
    homeEducation: {
      label: "Tasmania — Home education",
      url: "https://www.decyp.tas.gov.au/learning/home-education/",
    },
    legalAid: {
      label: "Tasmania Legal Aid",
      url: "https://www.legalaid.tas.gov.au/",
    },
  },
  ACT: {
    tenancy: {
      label: "ACT Government — Rental laws in the ACT",
      url: "https://www.act.gov.au/housing-planning-and-property/renting/rental-laws-in-the-act",
    },
    socialHousing: {
      label: "ACT Government — Apply for public or community housing",
      url: "https://www.act.gov.au/housing/public-and-community-housing/apply-for-housing",
    },
    educationStart: {
      label: "ACT Education Directorate — Enrolling in a public school",
      url: "https://www.education.act.gov.au/public-school-life/enrolling-in-a-public-school",
    },
    homeEducation: {
      label: "ACT Education Directorate — Home education",
      url: "https://www.education.act.gov.au/schooling/home-education",
    },
    legalAid: {
      label: "Legal Aid ACT",
      url: "https://www.legalaidact.org.au/",
    },
  },
  NT: {
    tenancy: {
      label: "NT Consumer Affairs — Residential tenancies",
      url: "https://consumeraffairs.nt.gov.au/for-consumers/residential-tenancies",
    },
    socialHousing: {
      label: "NT Government — Apply for social housing",
      url: "https://nt.gov.au/property/public-housing/apply-for-housing",
    },
    educationStart: {
      label: "NT Government — Enrol your child in school",
      url: "https://nt.gov.au/learning/primary-and-secondary-students/enrol-your-child-in-school",
    },
    homeEducation: {
      label: "NT Government — Home education",
      url: "https://nt.gov.au/learning/primary-and-secondary-students/home-education",
    },
    legalAid: {
      label: "Legal Aid NT",
      url: "https://www.legalaid.nt.gov.au/",
    },
  },
};

const resolveAustraliaJurisdictionByNumber = (
  postcodeNumber: number,
): AustraliaJurisdictionCode | null => {
  if ((postcodeNumber >= 200 && postcodeNumber <= 299) || (postcodeNumber >= 2600 && postcodeNumber <= 2618) || (postcodeNumber >= 2900 && postcodeNumber <= 2920)) {
    return "ACT";
  }

  if ((postcodeNumber >= 800 && postcodeNumber <= 999)) {
    return "NT";
  }

  if ((postcodeNumber >= 1000 && postcodeNumber <= 2599) || (postcodeNumber >= 2620 && postcodeNumber <= 2899) || (postcodeNumber >= 2921 && postcodeNumber <= 2999)) {
    return "NSW";
  }

  if ((postcodeNumber >= 3000 && postcodeNumber <= 3999) || (postcodeNumber >= 8000 && postcodeNumber <= 8999)) {
    return "VIC";
  }

  if ((postcodeNumber >= 4000 && postcodeNumber <= 4999) || (postcodeNumber >= 9000 && postcodeNumber <= 9999)) {
    return "QLD";
  }

  if ((postcodeNumber >= 5000 && postcodeNumber <= 5799) || (postcodeNumber >= 5800 && postcodeNumber <= 5999)) {
    return "SA";
  }

  if ((postcodeNumber >= 6000 && postcodeNumber <= 6797) || (postcodeNumber >= 6800 && postcodeNumber <= 6999)) {
    return "WA";
  }

  if ((postcodeNumber >= 7000 && postcodeNumber <= 7799) || (postcodeNumber >= 7800 && postcodeNumber <= 7999)) {
    return "TAS";
  }

  return null;
};

export const resolveAustraliaJurisdictionCode = (
  postcode?: string | null,
): AustraliaJurisdictionCode | null => {
  const normalized = normalizeMemberPostalCode("AU", postcode);
  if (!normalized) {
    return null;
  }

  return resolveAustraliaJurisdictionByNumber(Number.parseInt(normalized, 10));
};

export const getAustraliaJurisdictionLabel = (
  postcode?: string | null,
) => {
  const jurisdictionCode = resolveAustraliaJurisdictionCode(postcode);
  return jurisdictionCode ? AUSTRALIA_JURISDICTION_LABELS[jurisdictionCode] : null;
};

export const getAustraliaAppendixLink = (
  label: string,
  postcode?: string | null,
): AustraliaAppendixLink | null => {
  const jurisdictionCode = resolveAustraliaJurisdictionCode(postcode);
  if (!jurisdictionCode) {
    return null;
  }

  const jurisdictionLinks = AUSTRALIA_JURISDICTION_LINKS[jurisdictionCode];

  switch (label) {
    case AUSTRALIA_APPENDIX_ROUTE_LABELS.tenancy:
      return jurisdictionLinks.tenancy;
    case AUSTRALIA_APPENDIX_ROUTE_LABELS.socialHousing:
      return jurisdictionLinks.socialHousing;
    case AUSTRALIA_APPENDIX_ROUTE_LABELS.educationStart:
      return jurisdictionLinks.educationStart;
    case AUSTRALIA_APPENDIX_ROUTE_LABELS.homeEducation:
      return jurisdictionLinks.homeEducation;
    case AUSTRALIA_APPENDIX_ROUTE_LABELS.legalAid:
      return jurisdictionLinks.legalAid;
    default:
      return null;
  }
};
